import { useCallback } from 'react';
import { Platform } from 'react-native';
import { getSyncingStatus, getUser } from 'actions';
import { muteAllButThrows, useEventBusDispatch, useFetchOld } from 'hooks';
import { useAppDispatch, useAppStore } from 'store/hooks';
import Sentry from 'utils/sentry';
import { SubscriptionPlatform } from '../entities';
import { IAP_PURCHASE_FLOW_ENDED_WITH_SUCCESS, iapPurchaseFlowEndedWithError, setIsEligibleForIntroOffer, validatePurchase, } from '../actions';
import { finishSubscriptionPurchase, getAppleTransactionReceipt } from '../iap';
import { logPurchaseCompleteEvent } from '../utils/analytics';
import { selectShouldSeePremiumTrialScreen } from '../selectors';
import { bundleId } from '../../../constants';
const platformToStore = {
    [SubscriptionPlatform.ios]: 'apple',
    [SubscriptionPlatform.android]: 'google',
    [SubscriptionPlatform.amazon]: undefined,
};
const processedInCurrentFlow = new Set();
export const resetProcessedPurchases = () => {
    processedInCurrentFlow.clear();
};
/**
 * Validates a store purchase with the backend, acknowledges it with the store
 * and reports the flow as ended.
 */
export const useProcessIapPurchase = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const eventBusDispatch = useEventBusDispatch();
    const [, , , fetch] = useFetchOld(muteAllButThrows);
    return useCallback(async (purchase) => {
        if (processedInCurrentFlow.has(purchase.id))
            return;
        processedInCurrentFlow.add(purchase.id);
        const currentSubscription = store.getState().iapSubscriptions.purchasingSubscription;
        const isTrial = currentSubscription?.subscription.tier
            ? selectShouldSeePremiumTrialScreen(store.getState(), currentSubscription?.subscription.tier)
            : undefined;
        const { isEligibleForIntroOffer } = store.getState().iapSubscriptions;
        const { transactionId, purchaseToken, productId } = purchase;
        const { dataAndroid } = purchase;
        const { subscription, discount, analytics } = currentSubscription ?? {};
        const { price, currency } = subscription ?? {};
        const discountedPrice = (() => {
            if (subscription?.platform === SubscriptionPlatform.ios) {
                if (discount?.type === SubscriptionPlatform.ios) {
                    return subscription?.discounts?.find((subDis) => subDis.identifier === discount.discount.identifier)?.price;
                }
                if (subscription?.introductoryPriceAsAmountIOS &&
                    isEligibleForIntroOffer) {
                    return subscription.introductoryPriceAsAmountIOS;
                }
            }
            return undefined;
        })();
        const isApplePlatform = Platform.OS === 'ios';
        try {
            const appleBody = {
                productId,
                price,
                currency,
                transactionId: transactionId ?? undefined,
                transactionReceipt: isApplePlatform
                    ? await getAppleTransactionReceipt()
                    : undefined,
                discountedPrice,
                offerId: discount?.type === SubscriptionPlatform.ios
                    ? discount.discount.identifier
                    : undefined,
            };
            const androidBody = {
                productId,
                price,
                currency,
                packageName: bundleId,
                orderId: dataAndroid ? JSON.parse(dataAndroid).orderId : undefined,
                purchaseToken: purchaseToken ?? undefined,
                offerId: discount?.type === SubscriptionPlatform.android
                    ? discount.offer.offerId ?? undefined
                    : undefined,
            };
            let body = {};
            if (isApplePlatform) {
                body = appleBody;
            }
            else if (Platform.OS === 'android') {
                body = androidBody;
            }
            await fetch(validatePurchase(body));
            await finishSubscriptionPurchase(purchase);
        }
        catch (err) {
            // Let a retry of the same transaction through.
            processedInCurrentFlow.delete(purchase.id);
            Sentry.captureException(err);
            dispatch(iapPurchaseFlowEndedWithError());
            return;
        }
        // The purchase is validated and acknowledged from here on, so a failure
        // below must not re-arm the guard or report the purchase as failed.
        logPurchaseCompleteEvent(analytics, productId, subscription?.platform
            ? platformToStore[subscription.platform]
            : undefined, isTrial, (discount?.type === SubscriptionPlatform.ios
            ? discount.discount.identifier
            : discount?.offer.offerId) || undefined);
        try {
            await fetch(getUser());
        }
        catch (err) {
            Sentry.captureException(err);
        }
        dispatch(getSyncingStatus());
        dispatch(setIsEligibleForIntroOffer(false));
        eventBusDispatch(IAP_PURCHASE_FLOW_ENDED_WITH_SUCCESS, {
            productId,
            newSku: subscription
                ? {
                    id: subscription.id,
                    tier: subscription.tier,
                    period: subscription.period,
                }
                : undefined,
        });
    }, [dispatch, eventBusDispatch, fetch, store]);
};
export default useProcessIapPurchase;
